import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, browserLocalPersistence, setPersistence } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics'
import { getStorage } from 'firebase/storage'
import { firebaseConfig } from '../config/firebase.config'

// Debug environment variables in development
if (process.env.NODE_ENV === 'development') {
  console.log('Environment variables:', {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? 'Set' : 'Not set',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ? 'Set' : 'Not set',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ? 'Set' : 'Not set',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ? 'Set' : 'Not set',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ? 'Set' : 'Not set',
    appId: process.env.REACT_APP_FIREBASE_APP_ID ? 'Set' : 'Not set'
  })
}

// Validate Firebase configuration
const validateConfig = (config) => {
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ]

  const missingFields = requiredFields.filter(field => !config[field])
  
  if (missingFields.length > 0) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Current config:', {
        ...config,
        apiKey: config.apiKey ? 'Set' : 'Not set'
      })
      console.error('Please ensure your .env.local file:')
      console.error('1. Is in the root directory (solar/)')
      console.error('2. Has no spaces around = signs')
      console.error('3. Starts with REACT_APP_')
      console.error('4. Contains all required values')
    }
    throw new Error(
      `Missing Firebase configuration: ${missingFields.join(', ')}. ` +
      'Please check your .env.local file and ensure all required Firebase configuration values are set.'
    )
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Initialize Auth with custom settings
const auth = getAuth(app)
auth.useDeviceLanguage()

// Set persistence to LOCAL
setPersistence(auth, browserLocalPersistence)
  .catch((error) => {
    console.error("Auth persistence error:", error);
  });

// Initialize Google Auth Provider with custom parameters
// Keep minimal configuration to avoid auth/internal-error issues
const googleProvider = new GoogleAuthProvider()

// Add calendar scopes to request permissions upfront
googleProvider.addScope('https://www.googleapis.com/auth/calendar')

// Minimal custom parameters - only what's essential
// Note: Too many parameters can cause auth/internal-error
googleProvider.setCustomParameters({
  prompt: 'consent'
  // Removed: access_type, include_granted_scopes, hd - these can cause internal errors
  // Domain restriction is handled in the sign-in callback instead
})

// Initialize other services
const db = getFirestore(app)
let analytics = null

if (typeof window !== 'undefined') {
  isAnalyticsSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(app)
      }
    })
    .catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Firebase analytics initialization skipped:', error)
      }
    })
}

const storage = getStorage(app)

export { auth, db, analytics, storage, googleProvider }
export default app 